import {
  ExportButton,
  ImportExportHistoryEncoding,
} from '@/components/ExportButton/ExportButton';
import { FunctionHeader } from '@/components/FunctionHeader';
import {
  useReceivedInvoiceTransactionOcr,
  useReceivedInvoiceTransactionOcrCsv,
} from '@/context/services/invoice/ReceivedInvoice.service';
import {
  ReceivedInvoiceDetail,
  ReceivedInvoiceTransactionOcrStatus,
} from '@/context/services/invoice/type';
import { useReceivedInvoiceTransactionFlag } from '@/hooks/feature_flag/useReceivedInvoiceTransactionFlag';
import { useDownload } from '@/hooks/useDownload';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  IconError,
  IconPending,
  Tag,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import styles from './ReceivedInvoiceTransaction.module.scss';
import { useTransactionOcrConfirmModal } from './hooks/useTransactionOcrConfirmModal';

const cx = classnames.bind(styles);

export type Props = {
  data: ReceivedInvoiceDetail;
};

type TransactionOcrTag = {
  label: string;
  customIcon?: ReactNode;
  status?: 'success' | 'error';
};

const ReceivedInvoiceTransactionInner: FC<Props> = memo(({ data }) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();
  const { mutateAsync } = useReceivedInvoiceTransactionOcr();
  const getTransactionOcrCsv = useReceivedInvoiceTransactionOcrCsv();
  const download = useDownload();
  const { confirm, ConfirmModal } = useTransactionOcrConfirmModal();
  const [isLoading, setIsLoading] = useState(false);
  const invoiceId = data.id;
  const transactionOcrStatus = data.transaction_ocr_status;

  const showErrorToast = useCallback(
    (title: string) => {
      notify.error({
        title,
        duration: 5,
      });
    },
    [notify]
  );

  const onStartOcr = useCallback(async () => {
    if (!invoiceId) return;

    const confirmed = await confirm();
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const result = await mutateAsync({ id: invoiceId });
      if (result.isSuccessful) {
        notify.success({
          title: t('received_invoice_transaction_request_success'),
          duration: 5,
        });
      } else {
        showErrorToast(result.messages.join('\n'));
      }
    } catch (err) {
      // Explicitly notify Rollbar so that errors originating from the backend are not exposed, and return system_error to the screen.
      send(err instanceof Error ? err : String(err));
      showErrorToast(t('system_error'));
    } finally {
      setIsLoading(false);
    }
  }, [confirm, invoiceId, mutateAsync, notify, showErrorToast, t, send]);

  const tagProps = useMemo<TransactionOcrTag | null>(() => {
    switch (transactionOcrStatus) {
      case ReceivedInvoiceTransactionOcrStatus.processing:
        return {
          label: t('received_invoice_transaction_status_processing'),
          customIcon: <IconPending size={12} />,
          status: 'success',
        };
      case ReceivedInvoiceTransactionOcrStatus.failure:
        return {
          label: t('received_invoice_transaction_status_failure'),
          customIcon: <IconError size={12} />,
          status: 'error',
        };
      case ReceivedInvoiceTransactionOcrStatus.success:
        return {
          label: t('invoice_ocr_read_tag'),
        };
      default:
        // No status means transaction OCR has not been requested yet and is available to start.
        return null;
    }
  }, [t, transactionOcrStatus]);

  const onExport = useCallback(
    async (csvEncode: ImportExportHistoryEncoding) => {
      if (!invoiceId) return;

      try {
        const result = await getTransactionOcrCsv({
          id: invoiceId,
          csvEncode,
        });
        if (result.isSuccessful) {
          download({ value: result.blob, fileName: result.fileName });
        } else {
          showErrorToast(result.messages.join('\n'));
        }
      } catch (err) {
        send(err instanceof Error ? err : String(err));
        showErrorToast(t('export_csv.error_message'));
      }
    },
    [download, getTransactionOcrCsv, invoiceId, send, showErrorToast, t]
  );

  const isProcessing =
    transactionOcrStatus === ReceivedInvoiceTransactionOcrStatus.processing;
  const isSuccess =
    transactionOcrStatus === ReceivedInvoiceTransactionOcrStatus.success;

  return (
    <>
      <FunctionHeader
        className={cx(styles['container'])}
        functionName={t('received_invoice_transaction_title')}
        tag={
          tagProps ? (
            <Tag
              status={tagProps.status}
              closable={false}
              outline={false}
              customIcon={tagProps.customIcon}
              icon={false}
            >
              {tagProps.label}
            </Tag>
          ) : null
        }
        button={
          isSuccess ? (
            <div
              className={cx(styles['exportButtonContainer'])}
              data-testid='received-invoice-transaction-export-container'
            >
              <ExportButton onExport={onExport} size='xs' />
            </div>
          ) : (
            <ButtonV2
              isSecondary
              size='xs'
              isDisabled={isProcessing}
              isLoading={isLoading}
              onClick={onStartOcr}
              testId='start-received-invoice-transaction-ocr'
            >
              {t('received_invoice_transaction_start_ocr')}
            </ButtonV2>
          )
        }
        testId='received-invoice-transaction-header'
      />
      <ConfirmModal />
    </>
  );
});

ReceivedInvoiceTransactionInner.displayName = 'ReceivedInvoiceTransactionInner';

export const ReceivedInvoiceTransaction: FC<Props> = memo((props) => {
  const flag = useReceivedInvoiceTransactionFlag();

  if (!flag) return null;

  return <ReceivedInvoiceTransactionInner {...props} />;
});

ReceivedInvoiceTransaction.displayName = 'ReceivedInvoiceTransaction';
